import { AlertTitle, Collapse, Stack } from "@mui/material"
import { ReactElement, useState } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"

import { unrecognisedError } from "../../../errors/recognisedErrors"
import { INSTALLER_SINGLE_APPLICATION_BASE } from "../../../router/routes"
import useLaunchAmendment from "../../../services/middleware/hooks/amendApplication/useLaunchAmendment"
import { scrollToTop } from "../../../utils/submitApplication/submitApplicationUtils"
import AppButton from "../../buttons/appButton"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import ErrorDisplay from "../../fallback/errorDisplay"
import CreateApplicationSkeleton from "../../loading/skeletons/createApplicationSkeleton"
import ApplicationOrchestrator from "../single/applicationGenerator/applicationOrchestrator"
import ApplicationProvider from "../single/applicationProvider"

interface IAmendApplicationUrlParams {
    applicationId: string
}

// TODO - sort out the set display select lct

const AmendSingleApplication = (): ReactElement => {
    const { applicationId } = useParams<
        keyof IAmendApplicationUrlParams
    >() as IAmendApplicationUrlParams
    const [hideAlert, setHideAlert] = useState<boolean>(false)
    const navigate = useNavigate()

    // Get applicationStatus from query parameters
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const applicationStatus = searchParams.get("applicationStatus") || undefined

    const { isLoading, error, data } = useLaunchAmendment(applicationId)
    if (isLoading) return <CreateApplicationSkeleton />
    if (error) return <ErrorDisplay error={error} />
    if (!data) return <ErrorDisplay error={unrecognisedError()} />
    return (
        <ApplicationProvider
            rawApplicationSpec={data.rawApplicationSpec}
            applicationStepIndexMap={data.applicationStepIndexMap}
            applicationForms={data.applicationForms}
            formTitlesIndexMap={data.formTitlesIndexMap}
            onClickBackFirstStep={(): void => {
                navigate(
                    `${INSTALLER_SINGLE_APPLICATION_BASE}/${applicationId}`
                )
                scrollToTop(document)
            }}
            fixedInputs={data.fixedInputs}
            lcts={data.lcts}
            defaultFormData={data.formData}
            defaultSupplementingInfo={data.supplementingInfo}
            isAmendmentMode
            amendmentApplicationId={applicationId}
            applicationStatus={applicationStatus}
        >
            <Stack spacing={2}>
                <Collapse in={!hideAlert && !!data.devicesNotFound.length}>
                    <AppAlert
                        action={
                            <AppButton
                                color="inherit"
                                onClick={(): void => setHideAlert(true)}
                                size="small"
                            >
                                Hide
                            </AppButton>
                        }
                        severity="warning"
                    >
                        <AlertTitle>
                            Some of the devices in this application are no
                            longer available
                        </AlertTitle>
                        The following{" "}
                        {data.devicesNotFound.length === 1
                            ? "device is"
                            : "devices are"}{" "}
                        no longer available on the ENA Connect Direct live
                        device register: {data.devicesNotFound.join(", ")} .
                        This is likely because{" "}
                        {data.devicesNotFound.length === 1
                            ? "it has"
                            : "they have"}{" "}
                        been removed from the register by the manufacturer. We
                        have removed{" "}
                        {data.devicesNotFound.length ? "them" : "it"} from the
                        application form. In order to submit an amendment for
                        this application, you will be required to select a
                        different device
                    </AppAlert>
                </Collapse>
                <ApplicationOrchestrator />
            </Stack>
        </ApplicationProvider>
    )
}

export default AmendSingleApplication
